import type { ComputedRef } from "vue";
import type { WebsitePageFragment } from "~/graphql/generated";
import type { HreflangLink } from "~/composables/useHrefLangs";
import { normalizeHref } from "~/composables/useHrefLangs";

type ContentTranslation = {
  path: string;
  languageCode: string;
};

/**
 * Computes hreflang link tags for a single piece of content (a website page
 * or blog post), using the actual linked translations returned by the
 * backend instead of guessing alternates from the route/locale.
 *
 * `pathPrefix` accounts for pages whose browsable URL isn't the bare
 * backend `path` (e.g. blog posts live at `/blog${path}`, matching how
 * BlogPostIndexItem.vue builds its links).
 */
export default function useContentHrefLangs(
  content: ComputedRef<WebsitePageFragment | undefined>,
  pathPrefix = "",
) {
  const host = useCurrentHost().value;
  const baseUrl = useRequestURL();
  const isPromoOrMyshopi = host === "promopromo" || host === "myshopi";
  const localePath = useLocalePath();
  const { locales } = useI18n();

  // Backend `nl` is shared between Dutch (nl-NL, allefolders) and Flemish
  // (nl, promopromo/myshopi) — disambiguate using which brand is serving it.
  const toNuxtLocale = (languageCode: string) => {
    if (languageCode === "nl") return isPromoOrMyshopi ? "nl" : "nl-NL";
    return languageCode;
  };

  // hreflang must be the BCP-47 tag configured per locale (e.g. "fr-FR"),
  // not our internal Nuxt locale code (e.g. "fr") -- pull it from the same
  // i18n config layouts/default.vue relies on, so both stay in sync.
  const toHreflang = (nuxtLocale: string) =>
    locales.value.find(
      (candidate): candidate is { code: string; language?: string } =>
        typeof candidate === "object" && candidate.code === nuxtLocale,
    )?.language ?? nuxtLocale;

  const currentHrefLangs = computed<HreflangLink[]>(() => {
    if (!content.value) return [];

    const entries: ContentTranslation[] = [
      { path: content.value.path, languageCode: content.value.languageCode },
      ...content.value.translations,
    ];

    const links: HreflangLink[] = entries.map((entry) => {
      const nuxtLocale = toNuxtLocale(entry.languageCode);
      const hreflang = toHreflang(nuxtLocale);
      return {
        hid: `hreflang-${hreflang}`,
        rel: "alternate",
        hreflang,
        href: normalizeHref(
          localePath(`${pathPrefix}${entry.path}`, nuxtLocale),
          host,
          baseUrl,
        ),
      };
    });

    // No linked translation: fall back to a self-referential hreflang only.
    if (links.length === 1) return links;

    const defaultLink =
      links.find((link) => link.hreflang === "nl-NL") ?? links[0];

    return [
      ...links,
      { ...defaultLink, hid: "hreflang-x-default", hreflang: "x-default" },
    ];
  });

  return { currentHrefLangs };
}
